import { useCallback, useEffect, useState } from 'react'
import { reportedPostRepository } from '@/data/reports/repositories/reported_post_repository_impl'
import type { ReportedPost } from '@/domain/reports/entities/reported_post.entity'
import { DeleteReportedPostUseCase } from '@/domain/reports/usecases/delete_reported_post.usecase'
import { ListReportedPostsUseCase } from '@/domain/reports/usecases/list_reported_posts.usecase'
import { toAppError } from '@/core/errors/app_error'
import { getAxiosLikeErrorMessage } from '@/services/apiErrors'
import { logError } from '@/utils/logger'
import { matchReportedPost } from '@/presentation/shared/table/search_matchers'
import { useTableQueryState } from '@/presentation/shared/table/use_table_query_state'

const listReportedPosts = new ListReportedPostsUseCase(reportedPostRepository)
const deleteReportedPost = new DeleteReportedPostUseCase(reportedPostRepository)

export function useReportedPostsViewModel() {
  const [reportedPosts, setReportedPosts] = useState<ReportedPost[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    postId: null as string | null,
    postContent: '',
  })
  const [isDeleting, setIsDeleting] = useState(false)

  const refresh = useCallback(async () => {
    try {
      setIsLoading(true)
      setLoadError(null)
      setReportedPosts(await listReportedPosts.execute())
    } catch (error: unknown) {
      logError('Failed to load reported posts', error)
      setLoadError(toAppError(error, 'Failed to load reported posts').message)
      setReportedPosts([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const { table, actions: tableActions } = useTableQueryState({
    items: reportedPosts,
    matchesSearch: matchReportedPost,
  })

  const openDeleteDialog = useCallback((postId: string, postContent: string) => {
    setDeleteDialog({
      isOpen: true,
      postId,
      postContent: postContent.length > 50 ? `${postContent.substring(0, 50)}...` : postContent,
    })
  }, [])

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialog({ isOpen: false, postId: null, postContent: '' })
  }, [])

  const confirmDelete = useCallback(async () => {
    if (!deleteDialog.postId) return
    try {
      setIsDeleting(true)
      await deleteReportedPost.execute(deleteDialog.postId)
      setReportedPosts(prev => prev.filter(post => post.id !== deleteDialog.postId))
      closeDeleteDialog()
    } catch (error: unknown) {
      logError('Failed to delete reported post:', error)
      alert(
        getAxiosLikeErrorMessage(error, 'Failed to delete reported post. Please try again.')
      )
    } finally {
      setIsDeleting(false)
    }
  }, [deleteDialog.postId, closeDeleteDialog])

  return {
    state: {
      reportedPosts,
      loadError,
      filteredReportedPosts: table.filteredItems,
      paginatedReportedPosts: table.paginatedItems,
      searchTerm: table.searchTerm,
      entriesPerPage: table.entriesPerPage,
      currentPage: table.currentPage,
      sortField: table.sortField,
      sortDirection: table.sortDirection,
      deleteDialog,
      totalPages: table.totalPages,
      startIndex: table.startIndex,
      endIndex: table.endIndex,
    },
    isLoading,
    isDeleting,
    actions: {
      setSearch: tableActions.setSearch,
      setPageSize: tableActions.setPageSize,
      changePage: tableActions.changePage,
      goToPreviousPage: tableActions.goToPreviousPage,
      goToNextPage: tableActions.goToNextPage,
      handleSort: tableActions.handleSort,
      openDeleteDialog,
      closeDeleteDialog,
      confirmDelete,
    },
  }
}
